/**
 * Turns a level number into a complete, deterministic tower.
 *
 * Pure logic: no Phaser imports. Seed is the level number (plus an optional profile
 * salt for replays), so level 30 is the same tower for every player, every time.
 */
import { SAFE_X_MAX, SAFE_X_MIN, STAGE_CENTER_X } from '../types';
import type {
  BackgroundTheme,
  GradeBand,
  LevelConfig,
  PlatformKind,
  PlatformSpec,
  PlatformTheme,
  SentenceSpot,
  WordKind,
} from '../types';
import { WORD_LISTS } from '../data/words';
import { MIXED_PLATFORM_POOL, themeBandFor } from '../data/themes';
import { bonusDistanceFor } from './PowerUps';
import { mulberry32 } from './rng';
import type { Rng } from './rng';
import { bandProgress, buildWordBank, GRADE_BANDS, scoreWord, WordPicker } from './WordPicker';
import { SENTENCES, sentenceWords } from '../data/sentences';
import type { WordBank } from './WordPicker';

export const MIN_LEVEL = 1;
export const MAX_LEVEL = 100;

/** Levels 1-2 are springs only; jetpacks start at 3 (settled with the user). */
export const FIRST_JETPACK_LEVEL = 3;

/** Bonus platforms never sit in the first 3 or last 2 platforms (CLAUDE.md). */
export const BONUS_FIRST_INDEX = 3;
export const BONUS_LAST_OFFSET = 3;

/** Zig-zag amplitude around stage centre, in logical pixels. */
export const ZIGZAG_MIN = 110;
export const ZIGZAG_MAX = 240;

interface BandRow {
  readonly start: number;
  readonly end: number;
  readonly band: GradeBand;
  readonly wpm: number;
}

/** Difficulty bands, straight from the CLAUDE.md table. */
const BANDS: readonly BandRow[] = [
  { start: 1, end: 15, band: 3, wpm: 10 },
  { start: 16, end: 30, band: 4, wpm: 13 },
  { start: 31, end: 45, band: 5, wpm: 16 },
  { start: 46, end: 60, band: 6, wpm: 20 },
  { start: 61, end: 80, band: 7, wpm: 24 },
  { start: 81, end: 100, band: 8, wpm: 28 },
];

/** Capitalized proper nouns are allowed from level 46 only (CLAUDE.md). */
export const FIRST_CAPITALS_LEVEL = 46;

export function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return MIN_LEVEL;
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.floor(level)));
}

/** N = 20 at level 1, linear to 50 at level 30, then 50 for the rest. */
export function platformCountFor(level: number): number {
  const lvl = clampLevel(level);
  if (lvl >= 30) return 50;
  return Math.round(20 + ((lvl - 1) * 30) / 29);
}

function bandRowFor(level: number): BandRow {
  const lvl = clampLevel(level);
  const row = BANDS.find((b) => lvl >= b.start && lvl <= b.end);
  return row ?? (BANDS[BANDS.length - 1] as BandRow);
}

/** Which word grade a level draws from, and how far up that grade's list it is. */
export interface WordBandSpot {
  readonly band: GradeBand;
  /** 0 at the easy end of the grade's list, 1 at the hard end. */
  readonly progress: number;
  readonly wpm: number;
}

/**
 * Word difficulty for a level. With no grade, the original ramp: levels 1-15
 * are grade 3 words, and so on up the table. With a player's grade, that
 * grade gets its own whole ramp: the 100 levels are spread evenly from its
 * own grade's easiest words up to the hardest in the bank, so every player
 * plays every level and the climb keeps getting harder whatever they start
 * at (docs/WORDS_AND_SENTENCES.md, part 3).
 */
export function wordBandFor(level: number, grade: GradeBand | null = null): WordBandSpot {
  const lvl = clampLevel(level);
  if (grade === null) {
    const row = bandRowFor(lvl);
    return { band: row.band, progress: bandProgress(lvl, row.start, row.end), wpm: row.wpm };
  }
  const bands = GRADE_BANDS.length - GRADE_BANDS.indexOf(grade);
  const position = ((lvl - MIN_LEVEL) / (MAX_LEVEL - MIN_LEVEL)) * bands;
  const offset = Math.min(bands - 1, Math.floor(position));
  const band = GRADE_BANDS[GRADE_BANDS.indexOf(grade) + offset] ?? grade;
  const wpm = BANDS.find((row) => row.band === band)?.wpm ?? 10;
  return { band, progress: Math.min(1, position - offset), wpm };
}

export function gradeBandFor(level: number): GradeBand {
  return bandRowFor(level).band;
}

export function targetWpmFor(level: number): number {
  return bandRowFor(level).wpm;
}

/** 0-9: which 10-level visual band this level belongs to. */
export function themeIndexFor(level: number): number {
  return Math.ceil(clampLevel(level) / 10) - 1;
}

export interface LevelThemes {
  readonly backgroundTheme: BackgroundTheme;
  readonly platformTheme: PlatformTheme;
  readonly platformGlow: boolean;
}

export function themesFor(level: number): LevelThemes {
  const band = themeBandFor(level);
  return {
    backgroundTheme: band.background,
    platformTheme: band.platform,
    platformGlow: band.glow,
  };
}

/**
 * Early-level grace on par time: x1.25 at level 1, fading to x1.00 by level 21.
 */
export function graceFactorFor(level: number): number {
  const lvl = clampLevel(level);
  return 1 + 0.25 * Math.max(0, (21 - lvl) / 20);
}

/** Seconds to type one word at a target WPM, using the standard 5-chars-per-word. */
export function typingSeconds(chars: number, wpm: number): number {
  if (wpm <= 0) return 0;
  return (chars / 5 / wpm) * 60;
}

/**
 * Bonus count: of the integer counts whose share of the tower lands in 8-10%, take
 * one at random; if the integer grid offers none (small towers like N=29), take the
 * integer nearest 9%. Mean across all 100 levels is 9.0%.
 */
export function bonusCountFor(platformCount: number, rng: Rng): number {
  const candidates: number[] = [];
  for (let k = 1; k <= platformCount; k += 1) {
    const ratio = k / platformCount;
    if (ratio >= 0.08 - 1e-9 && ratio <= 0.1 + 1e-9) candidates.push(k);
  }
  if (candidates.length > 0) return rng.pick(candidates);
  return Math.max(1, Math.round(0.09 * platformCount));
}

/** Jetpacks are ~1/3 of bonuses, with seeded rounding so the long-run share holds. */
export function jetpackCountFor(bonusCount: number, level: number, rng: Rng): number {
  if (bonusCount <= 0 || clampLevel(level) < FIRST_JETPACK_LEVEL) return 0;
  const exact = bonusCount / 3;
  const base = Math.floor(exact);
  const extra = rng.chance(exact - base) ? 1 : 0;
  return Math.min(bonusCount, base + extra);
}

/**
 * Non-adjacent bonus indices inside [3, N-3]. Returned in draw order, so the
 * jetpack share is spread rather than clustered at the bottom.
 */
export function chooseBonusIndices(platformCount: number, count: number, rng: Rng): number[] {
  const first = BONUS_FIRST_INDEX;
  const last = platformCount - BONUS_LAST_OFFSET;
  if (count <= 0 || last < first) return [];

  const eligible: number[] = [];
  for (let i = first; i <= last; i += 1) eligible.push(i);

  const taken = new Set<number>();
  const chosen: number[] = [];
  const accept = (idx: number): void => {
    if (chosen.length >= count) return;
    if (taken.has(idx - 1) || taken.has(idx) || taken.has(idx + 1)) return;
    chosen.push(idx);
    taken.add(idx);
  };

  for (const idx of rng.shuffle(eligible)) accept(idx);
  // Greedy shuffling can leave a gap on tight towers; sweep in order to top up.
  if (chosen.length < count) for (const idx of eligible) accept(idx);

  return chosen;
}

function wordKindFor(kind: PlatformKind): WordKind {
  if (kind === 'spring') return 'moderate';
  if (kind === 'jetpack') return 'hard';
  return 'normal';
}

function clampX(x: number): number {
  return Math.min(SAFE_X_MAX, Math.max(SAFE_X_MIN, x));
}

let cachedDefaultBank: WordBank | null = null;

/** The curated grade banks (src/data/words), audited by `npm run audit:words`. */
export function defaultWordBank(): WordBank {
  cachedDefaultBank ??= buildWordBank(WORD_LISTS);
  return cachedDefaultBank;
}

export interface GenerateLevelOptions {
  readonly bank?: WordBank;
  /**
   * Word salt: each play of a level passes a fresh one, so the words differ
   * every time while the layout (platforms, springs, jetpacks) stays the
   * level's own. A fixed salt replays an exact level, for tests and dev links.
   */
  readonly salt?: number;
  /** False plays a sentence level as an ordinary word level (Tide Run is words only). */
  readonly sentences?: boolean;
  /** The player's grade, for their own word ramp; null or absent is the original ramp. */
  readonly grade?: GradeBand | null;
}

/** The last level of each world is a sentence level (docs/WORDS_AND_SENTENCES.md). */
export function isSentenceLevel(level: number): boolean {
  return clampLevel(level) % 10 === 0;
}

/** How many tries to fill a level with whole sentences before topping up with words. */
const SENTENCE_FILL_TRIES = 200;

/**
 * Sentences whose words add up to exactly `count`, each used once, in a random
 * order. Each grade's sentence lengths span a wide enough range that this
 * almost always succeeds; if it does not, the closest fit is returned and the
 * caller tops up the last few platforms with ordinary words.
 */
export function fillWithSentences(sentences: readonly string[], count: number, rng: Rng): string[] {
  const lengthOf = (sentence: string): number => sentenceWords(sentence).length;
  const shortest = Math.min(...sentences.map(lengthOf));
  let best: string[] = [];
  let bestWords = 0;
  for (let attempt = 0; attempt < SENTENCE_FILL_TRIES; attempt += 1) {
    const pool = rng.shuffle(sentences);
    const chosen: string[] = [];
    let remaining = count;
    for (const sentence of pool) {
      const length = lengthOf(sentence);
      // Take it if it finishes the level, or leaves room for at least one more.
      if (length === remaining || remaining - length >= shortest) {
        chosen.push(sentence);
        remaining -= length;
      }
      if (remaining === 0) return chosen;
    }
    if (count - remaining > bestWords) {
      best = chosen;
      bestWords = count - remaining;
    }
  }
  return best;
}

/** The word stream's seed: the level and the salt, mixed so nearby values differ. */
export function wordSeedFor(level: number, salt: number): number {
  return (Math.imul(level, 2654435761) ^ Math.imul(salt + 0x9e3779b9, 40503)) >>> 0;
}

/** Capitals (proper nouns) come with grade 6 words, which start at level 46 on the original ramp. */
function levelPicker(
  spot: { band: GradeBand; progress: number },
  bank: WordBank,
  rng: Rng,
): WordPicker {
  return new WordPicker(bank, spot.band, spot.progress, rng, {
    allowCapitals: spot.band >= gradeBandFor(FIRST_CAPITALS_LEVEL),
  });
}

/**
 * Two RNG streams. The layout stream is seeded by the level alone and its
 * draw order is fixed (re-baseline tests if it changes): bonus count ->
 * jetpack count -> bonus indices -> starting zig-zag side -> per-platform x
 * (and mixed theme). The word stream is seeded by level and salt, and draws
 * words from platform 1 upward.
 */
export function generateLevel(level: number, options: GenerateLevelOptions = {}): LevelConfig {
  const lvl = clampLevel(level);
  const rng = mulberry32(lvl);
  const wordRng = mulberry32(wordSeedFor(lvl, options.salt ?? 0));
  const bank = options.bank ?? defaultWordBank();
  const words = wordBandFor(lvl, options.grade ?? null);
  const platformCount = platformCountFor(lvl);
  const { backgroundTheme, platformTheme, platformGlow } = themesFor(lvl);

  const bonusCount = bonusCountFor(platformCount, rng);
  const jetpacks = jetpackCountFor(bonusCount, lvl, rng);
  const bonusIndices = chooseBonusIndices(platformCount, bonusCount, rng);

  const bonusKinds = new Map<number, PlatformKind>();
  bonusIndices.forEach((index, i) => {
    bonusKinds.set(index, i < jetpacks ? 'jetpack' : 'spring');
  });

  // Sentence levels keep the layout draws (so the layout is the level's own)
  // but have no springs or jetpacks: skipping words would break a sentence.
  const sentenceLevel = options.sentences !== false && isSentenceLevel(lvl);
  const kindFor = (index: number): PlatformKind => {
    if (index === 0) return 'start';
    if (index === platformCount - 1) return 'goal';
    if (sentenceLevel) return 'normal';
    return bonusKinds.get(index) ?? 'normal';
  };

  // Pass 1: horizontal layout. Start and goal sit centred; the rest zig-zag.
  const startSide = rng.chance(0.5) ? 1 : -1;
  const xs: number[] = [];
  const themes: Array<PlatformTheme | undefined> = [];
  for (let i = 0; i < platformCount; i += 1) {
    const terminal = i === 0 || i === platformCount - 1;
    if (terminal) {
      xs.push(STAGE_CENTER_X);
    } else {
      const side = startSide * (i % 2 === 0 ? 1 : -1);
      xs.push(clampX(STAGE_CENTER_X + side * rng.int(ZIGZAG_MIN, ZIGZAG_MAX)));
    }
    themes.push(platformTheme === 'mixed' && !terminal ? rng.pick(MIXED_PLATFORM_POOL) : undefined);
  }

  // Pass 2: words, bottom to top. Platform 0 is the starting floe and has none.
  const picker = levelPicker(words, bank, wordRng);

  const platforms: PlatformSpec[] = [];
  let typingTotal = 0;
  const spots = sentenceLevel
    ? sentenceSpots(fillWithSentences(SENTENCES[words.band], platformCount - 1, wordRng))
    : [];
  if (sentenceLevel) picker.markUsed(spots.map((spot) => spot.words[spot.index] ?? ''));

  for (let i = 0; i < platformCount; i += 1) {
    const kind = kindFor(i);
    const x = xs[i] ?? STAGE_CENTER_X;
    const theme = themes[i];

    if (i === 0) {
      platforms.push({ index: i, x, kind });
      continue;
    }

    const spot = spots[i - 1];
    const sentenceWord = spot?.words[spot.index];
    if (spot !== undefined && sentenceWord !== undefined) {
      typingTotal += typingSeconds(sentenceWord.length, words.wpm);
      platforms.push({
        index: i,
        x,
        kind,
        word: sentenceWord,
        wordScore: scoreWord(sentenceWord),
        sentence: spot,
        ...(theme === undefined ? {} : { theme }),
      });
      continue;
    }

    // Word levels, and any platform a sentence level could not fill exactly.
    const picked = picker.pick(wordKindFor(kind));
    typingTotal += typingSeconds(picked.word.length, words.wpm);

    const bonusDistance =
      kind === 'spring' || kind === 'jetpack' ? bonusDistanceFor(kind, picked.topHalf) : undefined;

    platforms.push({
      index: i,
      x,
      kind,
      word: picked.word,
      wordScore: picked.score,
      ...(bonusDistance === undefined ? {} : { bonusDistance }),
      ...(theme === undefined ? {} : { theme }),
    });
  }

  return {
    level: lvl,
    platformCount,
    gradeBand: words.band,
    targetWpm: words.wpm,
    wordProgress: words.progress,
    backgroundTheme,
    platformTheme,
    platformGlow,
    platforms,
    bonusCount: sentenceLevel ? 0 : bonusIndices.length,
    parSeconds: Math.ceil(typingTotal * graceFactorFor(lvl)),
    sentenceLevel,
    // Grades 3 to 5 see capitals and full stops but need not type them.
    match: sentenceLevel && words.band <= 5 ? 'lenient' : 'strict',
  };
}

/** A fresh word for a platform, mid-level. */
export interface RefillWord {
  readonly word: string;
  readonly wordScore: number;
  readonly bonusDistance?: number;
}

export interface WordRefill {
  /** A word of the right difficulty for a platform of this kind, never one already in the level. */
  next(kind: PlatformKind): RefillWord;
}

/**
 * Replacement words for a level in play: after a miss, the platform the
 * animal drops back to gets a new word rather than one already typed right.
 * Draws from the same difficulty windows as the level itself, and treats
 * every word already in the level as used.
 */
export function wordRefillFor(
  config: LevelConfig,
  salt: number,
  bank = defaultWordBank(),
): WordRefill {
  const lvl = clampLevel(config.level);
  const spot = { band: config.gradeBand, progress: config.wordProgress };
  const picker = levelPicker(spot, bank, mulberry32(wordSeedFor(lvl, salt) ^ 0x5bd1e995));
  picker.markUsed(config.platforms.map((platform) => platform.word ?? ''));
  return {
    next(kind: PlatformKind): RefillWord {
      const picked = picker.pick(wordKindFor(kind));
      const bonusDistance =
        kind === 'spring' || kind === 'jetpack'
          ? bonusDistanceFor(kind, picked.topHalf)
          : undefined;
      return {
        word: picked.word,
        wordScore: picked.score,
        ...(bonusDistance === undefined ? {} : { bonusDistance }),
      };
    },
  };
}

/** One spot per word, sentence after sentence, climbing order. */
function sentenceSpots(sentences: readonly string[]): SentenceSpot[] {
  return sentences.flatMap((sentence) => {
    const words = sentenceWords(sentence);
    return words.map((_, index) => ({ words, index }));
  });
}
